import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Plus, Trash2, User, Clock, Settings } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import ManufacturingService from '@/api/manufacturing.service';
import { useToast } from '@/hooks/use-toast';

const workOrderSchema = z.object({
  workOrderNumber: z.string().min(1, 'Work order number is required'),
  productionJobId: z.number().min(1, 'Production job is required'),
  operation: z.string().min(1, 'Operation is required'),
  description: z.string().optional(),
  assignedTo: z.string().optional(),
  estimatedHours: z.number().min(0.1, 'Estimated hours must be greater than 0'),
  scheduledStartDate: z.date({
    required_error: 'Scheduled start date is required',
  }),
  scheduledEndDate: z.date({
    required_error: 'Scheduled end date is required',
  }),
  notes: z.string().optional(),
});

type WorkOrderFormData = z.infer<typeof workOrderSchema>;

interface WorkOrderFormProps {
  initialData?: Partial<WorkOrderFormData & { id?: number; publicId?: string; status?: string }>;
  onSubmit: (data: WorkOrderFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  mode: 'create' | 'edit';
}

interface ProductionJob {
  id: number;
  publicId: string;
  jobNumber: string;
  productName: string;
  status: string;
}

interface Resource {
  id: string;
  name: string;
  type: 'machine' | 'employee' | 'tool';
  available: boolean;
}

const WorkOrderForm: React.FC<WorkOrderFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  mode
}) => {
  const { toast } = useToast();
  const [productionJobs, setProductionJobs] = useState<ProductionJob[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [selectedResources, setSelectedResources] = useState<string[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  const businessId = parseInt(localStorage.getItem('selectedBusiness') ? JSON.parse(localStorage.getItem('selectedBusiness')!).id : '1');

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset
  } = useForm<WorkOrderFormData>({
    resolver: zodResolver(workOrderSchema),
    defaultValues: {
      workOrderNumber: initialData?.workOrderNumber || '',
      productionJobId: initialData?.productionJobId || 0,
      operation: initialData?.operation || '',
      description: initialData?.description || '',
      assignedTo: initialData?.assignedTo || '',
      estimatedHours: initialData?.estimatedHours || 1,
      scheduledStartDate: initialData?.scheduledStartDate ? new Date(initialData.scheduledStartDate) : new Date(),
      scheduledEndDate: initialData?.scheduledEndDate ? new Date(initialData.scheduledEndDate) : new Date(),
      notes: initialData?.notes || '',
    }
  });

  const scheduledStartDate = watch('scheduledStartDate');
  const scheduledEndDate = watch('scheduledEndDate');

  useEffect(() => {
    fetchProductionJobs();
    fetchResources();
  }, [businessId]);

  useEffect(() => {
    if (initialData && mode === 'edit') {
      reset({
        workOrderNumber: initialData.workOrderNumber || '',
        productionJobId: initialData.productionJobId || 0,
        operation: initialData.operation || '',
        description: initialData.description || '',
        assignedTo: initialData.assignedTo || '',
        estimatedHours: initialData.estimatedHours || 1,
        scheduledStartDate: initialData.scheduledStartDate ? new Date(initialData.scheduledStartDate) : new Date(),
        scheduledEndDate: initialData.scheduledEndDate ? new Date(initialData.scheduledEndDate) : new Date(),
        notes: initialData.notes || '',
      });
    }
  }, [initialData, mode, reset]);

  const fetchProductionJobs = async () => {
    try {
      setLoadingJobs(true);
      const response = await ManufacturingService.getProductionJobs(businessId);
      if (response.data.result === 'success') {
        const activeJobs = response.data.productionJobs?.filter((job: ProductionJob) => 
          job.status === 'pending' || job.status === 'in_progress'
        ) || [];
        setProductionJobs(activeJobs);
      }
    } catch (error) {
      console.error('Error fetching production jobs:', error);
      toast({
        title: "Error",
        description: "Failed to fetch production jobs",
        variant: "destructive",
      });
    } finally {
      setLoadingJobs(false);
    }
  };

  const fetchResources = async () => {
    // Mock resources - in real app, this would come from an API
    const mockResources: Resource[] = [
      { id: '1', name: 'CNC Machine 1', type: 'machine', available: true },
      { id: '2', name: 'Assembly Line A', type: 'machine', available: true },
      { id: '3', name: 'John Smith', type: 'employee', available: true },
      { id: '4', name: 'Jane Doe', type: 'employee', available: false },
      { id: '5', name: 'Welding Tool Set', type: 'tool', available: true },
      { id: '6', name: 'Quality Inspector', type: 'employee', available: true },
    ];
    setResources(mockResources);
  };

  const generateWorkOrderNumber = () => {
    const timestamp = Date.now().toString().slice(-6);
    const randomNum = Math.floor(Math.random() * 100).toString().padStart(2, '0');
    return `WO-${timestamp}${randomNum}`;
  };

  const handleGenerateNumber = () => {
    setValue('workOrderNumber', generateWorkOrderNumber());
  };

  const handleResourceToggle = (resourceId: string) => {
    setSelectedResources(prev => 
      prev.includes(resourceId) 
        ? prev.filter(id => id !== resourceId)
        : [...prev, resourceId]
    );
  };

  const getResourceIcon = (type: string) => {
    switch (type) {
      case 'machine': return <Settings className="h-4 w-4" />;
      case 'employee': return <User className="h-4 w-4" />;
      case 'tool': return <Settings className="h-4 w-4" />;
      default: return <Settings className="h-4 w-4" />;
    }
  };

  const onFormSubmit = (data: WorkOrderFormData) => {
    // Validate date range
    if (data.scheduledEndDate <= data.scheduledStartDate) {
      toast({
        title: "Validation Error",
        description: "End date must be after start date",
        variant: "destructive",
      });
      return;
    }

    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Basic Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="workOrderNumber">Work Order Number</Label>
              <div className="flex gap-2">
                <Input
                  id="workOrderNumber"
                  {...register('workOrderNumber')}
                  placeholder="Enter work order number"
                  className={errors.workOrderNumber ? 'border-red-500' : ''}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGenerateNumber}
                  className="whitespace-nowrap"
                >
                  Generate
                </Button>
              </div>
              {errors.workOrderNumber && (
                <p className="text-sm text-red-500">{errors.workOrderNumber.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="productionJobId">Production Job</Label>
              <Select
                value={watch('productionJobId')?.toString() || ''}
                onValueChange={(value) => setValue('productionJobId', parseInt(value))}
              >
                <SelectTrigger className={errors.productionJobId ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Select production job" />
                </SelectTrigger>
                <SelectContent>
                  {loadingJobs ? (
                    <SelectItem value="loading" disabled>Loading jobs...</SelectItem>
                  ) : (
                    productionJobs.map((job) => (
                      <SelectItem key={job.id} value={job.id.toString()}>
                        {job.jobNumber} - {job.productName}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {errors.productionJobId && (
                <p className="text-sm text-red-500">{errors.productionJobId.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="operation">Operation</Label>
              <Input
                id="operation"
                {...register('operation')}
                placeholder="e.g., Cutting, Assembly, Welding"
                className={errors.operation ? 'border-red-500' : ''}
              />
              {errors.operation && (
                <p className="text-sm text-red-500">{errors.operation.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="assignedTo">Assigned To</Label>
              <Input
                id="assignedTo"
                {...register('assignedTo')}
                placeholder="Employee name or team"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              {...register('description')}
              placeholder="Detailed description of the work order"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Scheduling */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Scheduling
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Scheduled Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !scheduledStartDate && "text-muted-foreground",
                      errors.scheduledStartDate && "border-red-500"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {scheduledStartDate ? format(scheduledStartDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={scheduledStartDate}
                    onSelect={(date) => setValue('scheduledStartDate', date || new Date())}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              {errors.scheduledStartDate && (
                <p className="text-sm text-red-500">{errors.scheduledStartDate.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Scheduled End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !scheduledEndDate && "text-muted-foreground",
                      errors.scheduledEndDate && "border-red-500"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {scheduledEndDate ? format(scheduledEndDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={scheduledEndDate}
                    onSelect={(date) => setValue('scheduledEndDate', date || new Date())}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              {errors.scheduledEndDate && (
                <p className="text-sm text-red-500">{errors.scheduledEndDate.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="estimatedHours">Estimated Hours</Label>
              <Input
                id="estimatedHours"
                type="number"
                step="0.5"
                min="0.1"
                {...register('estimatedHours', { valueAsNumber: true })}
                className={errors.estimatedHours ? 'border-red-500' : ''}
              />
              {errors.estimatedHours && (
                <p className="text-sm text-red-500">{errors.estimatedHours.message}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resource Assignment */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Resource Assignment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {resources.map((resource) => (
              <div
                key={resource.id}
                className={cn(
                  "flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors",
                  selectedResources.includes(resource.id) 
                    ? "border-blue-500 bg-blue-50" 
                    : "border-gray-200 hover:border-gray-300",
                  !resource.available && "opacity-50 cursor-not-allowed"
                )}
                onClick={() => resource.available && handleResourceToggle(resource.id)}
              >
                <div className="flex items-center gap-2">
                  {getResourceIcon(resource.type)}
                  <div>
                    <p className="font-medium text-sm">{resource.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{resource.type}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={resource.available ? "default" : "secondary"}>
                    {resource.available ? "Available" : "Busy"}
                  </Badge>
                  {selectedResources.includes(resource.id) && (
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {selectedResources.length > 0 && (
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">
                Selected resources: {selectedResources.length}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notes */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            {...register('notes')}
            placeholder="Any additional notes or special instructions"
            rows={4}
          />
        </CardContent>
      </Card>

      {/* Form Actions */}
      <div className="flex justify-end gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Saving...' : mode === 'create' ? 'Create Work Order' : 'Update Work Order'}
        </Button>
      </div>
    </form>
  );
};

export default WorkOrderForm; 